// BudgetCommercialeCard.tsx
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../../redux/store";

import {
    Budget,
    fetchBudgetsCommerciali,
    BudgetsCommercialiList
} from "../budgetsSlice";

import { ErrorStatusTypes } from "components/common/errorsDeclarations";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { theme } from "../../../../theme";

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BudgetCommercialeMandatarioCard from "./mandatari/BudgetCommercialeMandatarioCard";

// Component per item con sfondo grigio
const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    padding: theme.spacing(2),
    textAlign: 'center',
    color: (theme.vars ?? theme).palette.text.secondary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    alignContent: 'center',
    alignItems: 'center',
    elevation: 6
}));

interface BudgetCommercialeCardProps {
    budget: Budget;
    stagione_id: number;
}

const BudgetCommercialeCard = ({ budget, stagione_id }: BudgetCommercialeCardProps) => {
    const dispatch = useAppDispatch();

    // Stati locali
    const [expanded, setExpanded] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // Selector per i dati dei mandatari
    const budgetsCommerciali = useSelector<RootState, BudgetsCommercialiList>(
        (state) => state.budgetsState.budgetsCommercialiList
    );

    // Handler per espandere/collassare
    const handleExpandClick = async () => {
        if (!expanded ) {
            // Prima volta che espando, carico i dati
            setLoading(true);
            try {
                await dispatch(fetchBudgetsCommerciali({
                    stagione_id: stagione_id,
                    commerciale_id: budget.commerciale
                }));
                setDataLoaded(true);
            } finally {
                setLoading(false);
            }
        }
        setExpanded(!expanded);
    };

    // Filtro i dati per questo commerciale specifico
    const mandatariCommerciale = budgetsCommerciali.budgetsCommerciali?.filter(
        bc => bc.commerciale === budget.commerciale
    );

    return (
        <Card sx={{
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
        }}>
            {/* Header con i totali del commerciale */}
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 3 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center" }}
                    >
                        <Typography variant="h6">
                            {budget.commerciale_display}
                        </Typography>
                        <IconButton
                            onClick={handleExpandClick}
                            size="small"
                            sx={{ ml: 1 }}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <Item elevation={2}>
                        <Typography variant="h6">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(budget.valore_budget) + " €"}
                        </Typography>
                    </Item>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <Item elevation={2}>
                        <Typography variant="h6">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(budget.valore_confermato) + " €"}
                        </Typography>
                    </Item>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <Item elevation={2}>
                        <Typography variant="h6">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(budget.valore_prodotto) + " €"}
                        </Typography>
                        {budget.percentuale_prodotto > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                ({budget.percentuale_prodotto.toFixed(1)}%)
                            </Typography>
                        )}
                    </Item>
                </Grid>
            </Grid>

            {/* Collapse per i dettagli dei mandatari */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2, pl: 4 }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 3
                        }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : (
                        <>
                            {mandatariCommerciale.length > 0 ? (
                                <Box>
                                    {mandatariCommerciale.map((budgetMandatario) => (
                                        <BudgetCommercialeMandatarioCard
                                            key={`${budgetMandatario.commerciale}-${budgetMandatario.mandatario}`}
                                            budgetMandatario={budgetMandatario}
                                            stagione_id={stagione_id}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ py: 2 }}
                                >
                                    Nessun dettaglio disponibile
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Collapse>
        </Card>
    );
};

export default BudgetCommercialeCard;